import { Box, Flex, type FlexProps, GridItem, SimpleGrid, Text } from "@chakra-ui/react";
import React from "react";
import { type PricingExtensionProps, type PricingDetailsProps } from "./data";

function CardContainer({
  children,
  title,
  align = "start",
  direction = "column",
}: {
  title: string;
  children: React.ReactNode;
} & Pick<FlexProps, "align" | "direction">) {
  return (
    <Flex
      align={align}
      bg="white"
      borderRadius={{ base: "smaller", sm: "small", md: "bigger" }}
      border="1px"
      borderColor="brand.border.extraLight"
      boxShadow="md"
      flexDir={direction}
      gap="bigger"
      fontSize="brand.sm"
      flex={1}
      w="100%"
      p={{ base: "small", sm: "big", md: "bigger", lg: "48px" }}
      pos="relative"
    >
      <Text as="h3" textStyle={{ base: "headline3", lg: "headline2" }}>
        {title}
      </Text>
      {children}
    </Flex>
  );
}

export function DetailCard({ title, features }: PricingDetailsProps) {
  return (
    <CardContainer title={title}>
      <SimpleGrid columns={{ base: 1, lg: 4 }} gap="big" w="100%">
        {features.map((feature) => (
          <GridItem
            key={feature.title}
            colSpan={{ base: 1, lg: feature.colSpan }}
            flexDir="column"
            _notLast={{
              borderRight: { base: "none", lg: "1px" },
              borderBottom: { base: "1px", lg: "none" },
              borderColor: { base: "brand.border.extraLight", lg: "brand.border.extraLight" },
              pr: { base: "0", lg: "big" },
              pb: { base: "big", lg: "0" },
            }}
          >
            <Text as="h4" textStyle="caption1" fontWeight="bold">
              {feature.title}
            </Text>

            <SimpleGrid
              as="ul"
              columns={{ base: 1, lg: feature.items.length > 4 ? 2 : 1 }}
              color="brand.textSecondary.light"
              flexDir="column"
              gap="big"
              mt="big"
              listStyleType="none"
            >
              {feature.items.map((item) => (
                <Text key={item} as="li" textStyle="caption1">
                  {item}
                </Text>
              ))}
            </SimpleGrid>
          </GridItem>
        ))}
      </SimpleGrid>
    </CardContainer>
  );
}

export function ExtensionCard({ title, features }: PricingExtensionProps) {
  return (
    <CardContainer title={title} align={{ base: "start", xl: "center" }} direction={{ base: "column", xl: "row" }}>
      <SimpleGrid columns={{ base: 1, lg: 3 }} gap={{ base: "bigger", lg: "smaller" }}>
        {features.map((feature) => (
          <Flex
            key={feature.title}
            align="center"
            gap={{ base: "smaller", xl: "small" }}
            maxW={{ base: "100%", xl: "240px" }}
            p={{ base: "0", lg: "small" }}
            pos="relative"
          >
            <Box as={feature.icon} boxSize={{ base: "icon.md", lg: "icon.lg", xl: "icon.xl" }} />
            <Text as="h4" textStyle="caption1">
              {feature.title}
            </Text>
          </Flex>
        ))}
      </SimpleGrid>
    </CardContainer>
  );
}
